import { useQuery } from "@apollo/client";
import { filter, map, pipe, sortBy, uniqBy } from "lodash/fp";
import { Suspense, useMemo, useState } from "react";

import isNotNullish from "lib/isNotNullish";
import Suspender from "lib/Suspender";
import { WorkspaceBrandNamesDoc, WorkspaceBrandNamesQuery } from "operations";

import {
  LoadingSkeleton,
  PrimaryPill,
  SecondaryPill,
} from "@joor/design-system";
import {
  extraRows,
  StyledContent,
  StyledLabel,
  StyledMenu,
  StyledMenuOptions,
  StyledTitle,
} from "./styledComponents";

const mapBrands = pipe(
  (data?: WorkspaceBrandNamesQuery) => data?.retailWorkspace?.assortments,
  map((assortment) => assortment.brand),
  filter(isNotNullish),
  uniqBy((brand) => brand.id),
  map((brand) => brand.alias || brand.node?.name || "Unknown Brand"),
  sortBy((value) => value)
);

const useBrands = (workspaceId: string) => {
  const { data, loading } = useQuery(WorkspaceBrandNamesDoc, {
    variables: { workspaceId: workspaceId },
  });
  const brands = useMemo(() => data && mapBrands(data), [data]);
  return { loading, brands };
};

export const Brands = ({
  workspaceId,
  moreCurrencies,
  numCurrencies,
}: {
  workspaceId: string;
  moreCurrencies: boolean;
  numCurrencies: number;
}) => {
  const { loading, brands } = useBrands(workspaceId);
  const [showBrands, setShowBrands] = useState(false);
  const onHover = () => {
    setShowBrands(true);
  };
  const onLeave = () => {
    setShowBrands(false);
  };

  const noBrand = !brands || brands?.length === 0;
  const oneBrand = brands && brands?.length === 1;
  const multipleBrands = brands && brands?.length > 1;

  return (
    <StyledContent>
      <StyledLabel>Brand</StyledLabel>
      <Suspense fallback={<LoadingSkeleton />}>
        <Suspender suspend={loading}>
          {multipleBrands && (
            <StyledTitle data-testid="brands-value">
              All Brands
              <div onMouseEnter={onHover} onMouseLeave={onLeave}>
                {showBrands ? (
                  <>
                    <SecondaryPill className="hover" variant="INACTIVE">
                      {brands.length}
                    </SecondaryPill>
                    <StyledMenu>
                      {brands.map((brand) => (
                        <StyledMenuOptions>
                          <PrimaryPill variant="INACTIVE">{brand}</PrimaryPill>
                        </StyledMenuOptions>
                      ))}
                    </StyledMenu>
                  </>
                ) : (
                  <PrimaryPill className="hover" variant="INACTIVE">
                    {brands.length}
                  </PrimaryPill>
                )}
              </div>
            </StyledTitle>
          )}
          {oneBrand && (
            <StyledTitle data-testid="brands-value">{brands[0]}</StyledTitle>
          )}
          {noBrand && <StyledTitle data-testid="brands-value">-</StyledTitle>}
          {moreCurrencies && extraRows(numCurrencies)}
        </Suspender>
      </Suspense>
    </StyledContent>
  );
};
